/**
 * Interface scale.
 *
 * The Attenuated tokens are deliberately absolute pixels (hairlines must stay on whole
 * pixels, the type scale is capped at seven steps), which at 100% reads small on the
 * side monitor this dashboard actually lives on. Rather than fork every token into a
 * rem system — and lose the whole-pixel hairline guarantee — the scale is applied as
 * `zoom` on the document root, which every current engine lays out properly (unlike
 * `transform: scale`, which only paints bigger).
 *
 * The preference is per-browser, not per-account: scale is a property of the monitor
 * you are looking at, so it lives in localStorage next to the sound toggle, and each
 * machine holds its own.
 */

import { useEffect, useState } from 'react';

import { isTimeFormat, type TimeFormat } from './format.ts';

const SCALE_KEY = 'nightshift.ui-scale';
const TIME_FORMAT_KEY = 'nightshift.time-format';
const TIME_FORMAT_EVENT = 'nightshift-time-format';

/** Percentages offered in Settings → Display. */
export const UI_SCALE_OPTIONS = [100, 115, 125, 135, 150] as const;

/** The operator asked for "30 to 40% more zoomed in" as the default; 135 splits it. */
const DEFAULT_UI_SCALE = 135;

export function uiScale(): number {
  try {
    const raw = Number(window.localStorage.getItem(SCALE_KEY));
    if ((UI_SCALE_OPTIONS as readonly number[]).includes(raw)) return raw;
  } catch {
    // Storage unavailable; the default applies.
  }
  return DEFAULT_UI_SCALE;
}

export function setUiScale(percent: number): void {
  try {
    window.localStorage.setItem(SCALE_KEY, String(percent));
  } catch {
    // The change still applies for this page lifetime; it simply will not persist.
  }
  applyUiScale(percent);
}

export function applyUiScale(percent = uiScale()): void {
  const root = document.documentElement;
  root.style.setProperty('zoom', String(percent / 100));
  // Surfaces that must measure true pixels (the Automation canvas: React Flow reads handle
  // geometry via getBoundingClientRect, which the root zoom inflates) counter the scale
  // with `zoom: var(--ui-zoom-inverse)` so their effective zoom is exactly 1.
  root.style.setProperty('--ui-zoom-inverse', String(100 / percent));
  root.style.removeProperty('width');
  /*
   * Firefox on Android (and some other engines) apply `zoom` as a layout shrink without
   * scaling the paint back to the visual viewport. 1/1.35 ≈ 0.74 — a dead black band to the
   * right of the app. Measure after the zoom lands; if the root no longer fills the window,
   * stretch its used width so the shrunken layout still occupies 100vw.
   */
  if (typeof window === 'undefined' || percent === 100) return;
  const fill = () => {
    const painted = root.getBoundingClientRect().width;
    const visual = window.innerWidth;
    if (visual > 0 && painted / visual < 0.92) {
      root.style.width = `${percent}vw`;
    }
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(fill);
  else fill();
}

const DEFAULT_TIME_FORMAT: TimeFormat = 'relative';

function timeFormatFromStorage(raw: string | null): TimeFormat {
  return isTimeFormat(raw) ? raw : DEFAULT_TIME_FORMAT;
}

export function timeFormat(): TimeFormat {
  try {
    return timeFormatFromStorage(window.localStorage.getItem(TIME_FORMAT_KEY));
  } catch {
    return DEFAULT_TIME_FORMAT;
  }
}

export function setTimeFormat(next: TimeFormat): void {
  try {
    window.localStorage.setItem(TIME_FORMAT_KEY, next);
  } catch {
    // The change still applies for this page lifetime; it simply will not persist.
  }
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event(TIME_FORMAT_EVENT));
  }
}

export function useTimeFormat(): TimeFormat {
  const [format, setFormat] = useState(timeFormat);
  useEffect(() => {
    const sync = () => setFormat(timeFormat());
    window.addEventListener(TIME_FORMAT_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(TIME_FORMAT_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return format;
}

export type ActivityDisplay = 'throbber' | 'timer';
const ACTIVITY_KEY = 'nightshift.activity-display';
const ACTIVITY_EVENT = 'nightshift-activity-display';
let activityFallback: ActivityDisplay = 'throbber';
export function activityDisplay(): ActivityDisplay {
  try { const value = window.localStorage.getItem(ACTIVITY_KEY); return value === 'timer' ? 'timer' : value === 'throbber' ? 'throbber' : activityFallback; }
  catch { return activityFallback; }
}
export function setActivityDisplay(next: ActivityDisplay): void {
  activityFallback = next;
  try { window.localStorage.setItem(ACTIVITY_KEY, next); } catch { /* Session preference still applies. */ }
  window.dispatchEvent(new Event(ACTIVITY_EVENT));
}
export function useActivityDisplay(): ActivityDisplay {
  const [mode, setMode] = useState(activityDisplay);
  useEffect(() => {
    const sync = () => setMode(activityDisplay());
    window.addEventListener(ACTIVITY_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener(ACTIVITY_EVENT, sync); window.removeEventListener('storage', sync); };
  }, []);
  return mode;
}
